/**
 * One node on the controller page: its valves, labels, relay taps and self test.
 * Split out of pages/irrigation/EspNowControllerDetail.tsx.
 */
import { useState, useEffect } from 'react';
import { Settings, Trash2, Upload, Clock, ClockAlert } from 'lucide-react';
import { useOrgTimezone, formatDateTimeInTz } from '@/utils/timezone';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import {
  type EspNowRouter,
  type MasterPump,
  type NodeScheduleState,
  type RelayScheduleState,
  getRelayState,
  relayCountFor,
  formatSecsOfDay,
  formatCountdown,
  formatDuration,
  clockSkewSec,
} from '../../../services/espnowIrrigation';
import { nodeNoun } from '../../../utils/nodeLabel';
import { type ValveStatus } from '../../../utils/liveMapStatus';
import {
  relayTile,
  relayCommandBusy,
  relayIntendedState,
  withScheduledTone,
  type RelayCommand,
} from '../../../utils/relayCommand';
import NodeSelfTest from '../NodeSelfTest';
import { MANUAL_RUN_MAX_LABEL, RELAY_TONE } from './relayTiming';

// ─── Router Detail Card ───────────────────────────────────────────────────────

export function RouterDetailCard({
  controllerId,
  transport,
  router,
  schedule,
  bridgeValves,
  timezoneOffsetSec,
  masterPumps,
  onSaveLabels,
  onRename,
  isSuperAdmin,
  canRemove,
  onRemove,
  onUpdateFirmware,
  otaBusy,
  controllerOnline,
  stateUnknown,
  onToggleRelay,
  relayCommands,
  onTestFinished,
}: {
  controllerId: string;
  transport?: string | null;
  router: EspNowRouter;
  schedule: NodeScheduleState | null;
  /**
   * What the BRIDGE's timeline says each of this node's valves is doing, keyed
   * `mac:relay`. Null on a site whose nodes hold their own timers — there the
   * node's own `schedule` snapshot is the better answer and this is not read.
   */
  bridgeValves: Map<string, ValveStatus> | null;
  timezoneOffsetSec: number;
  masterPumps: MasterPump[];
  onSaveLabels: (labels: Record<string, string>, relayCount?: number | null, disabledRelays?: number[]) => void;
  onRename: (name: string) => void;
  isSuperAdmin: boolean;
  /** Admin or super admin - the same gate the delete endpoint applies. */
  canRemove: boolean;
  onRemove: () => void;
  onUpdateFirmware: () => void;
  otaBusy: boolean;
  controllerOnline: boolean;
  /**
   * The bridge cannot currently reach the mesh, so `router.online` is the last
   * value it managed to read rather than the truth. Rendered as "unknown"
   * rather than offline: a red dot on every node is a specific, wrong claim —
   * that the nodes failed — when the actual fault is upstream of all of them.
   *
   * Note this is only the COORDINATOR-level unknown. A node can also be unknown
   * on its own account — nothing heard from it and nothing asked — which arrives
   * as router.state and is handled the same way, for the same reason.
   */
  stateUnknown: boolean;
  onToggleRelay: (relayIndex: number, currentlyOn: boolean) => void;
  /**
   * Manual taps still being followed, site-wide, keyed `mac:relay`. A tap is
   * three claims deep — queued, acknowledged by the site, reported by the node
   * — and the tile colours the last one that has actually arrived.
   */
  relayCommands: Map<string, RelayCommand>;
  /** A finished per-node test has new readings on the server; pull them in. */
  onTestFinished: () => void;
}) {
  const orgTz = useOrgTimezone();
  const noun = nodeNoun(transport);
  // Unverified, from either direction: the coordinator cannot be reached, or the
  // node itself has neither been heard from nor asked. Both mean "we do not
  // know", and neither is a fault to report.
  const unverified = stateUnknown || router.state === 'unknown';
  // Only ever true when something actually tried and failed.
  const failed = router.state ? router.state === 'offline' : (!router.online && !unverified);
  const [labels, setLabels] = useState<Record<string, string>>(router.relay_labels ?? {});
  const [editingLabels, setEditingLabels] = useState(false);
  const [countValue, setCountValue] = useState<number | null>(router.relay_count);
  // Relays the board has that are wired to nothing. Held as a Set while editing
  // so the checkboxes stay independent of the list's order.
  const [disabledValue, setDisabledValue] = useState<Set<number>>(
    () => new Set(router.disabled_relays ?? [])
  );
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(router.name ?? '');
  const pumpsOnThisRouter = masterPumps.filter(p => p.router_mac === router.router_mac);
  const isMasterRouter = pumpsOnThisRouter.length > 0;

  // Keep local state in sync with prop updates (e.g. after rename or auto-refresh)
  useEffect(() => {
    if (!editingName) setNameValue(router.name ?? '');
  }, [router.name, editingName]);

  useEffect(() => {
    if (!editingLabels) {
      setLabels(router.relay_labels ?? {});
      setCountValue(router.relay_count);
      setDisabledValue(new Set(router.disabled_relays ?? []));
    }
  }, [router.relay_labels, router.relay_count, router.disabled_relays, editingLabels]);

  // Only render relays the board physically has.
  //
  // A node's own schedule snapshot is the best answer when there is one, but an
  // XBee node never posts one - the bridge holds its timers - so that read as 8
  // forever, and a 4-relay board showed four controls that open nothing.
  // relayCountFor falls back on the operator-set count, then on the transport.
  const reportsOwnCount = (schedule?.relay_count ?? 0) > 0;
  const relayCount = schedule?.relay_count || relayCountFor(router);
  // While editing, the grid follows the box so rows appear and vanish as typed.
  const editRelayCount = editingLabels && !reportsOwnCount
    ? relayCountFor({ relay_count: countValue, transport: router.transport })
    : relayCount;
  const relayStates = Array.from({ length: relayCount }, (_, i) => getRelayState(router.relay_states, i));
  // Read once per render so every tile on the card ages its tap identically.
  // The page re-renders on its own 15 s poll and on every phase change, which
  // is often enough for a 90 s verdict to clear itself.
  const nowMs = Date.now();
  const activeCount = relayStates.filter(Boolean).length;

  // Relays to draw as controls. Out-of-use ones come off — they are the
  // invented-valve problem relay_count already solved, one level finer: a
  // 4-relay board wired on 0 and 3 has to keep saying 4, or channel 3 becomes
  // unreachable, so the two in the middle are subtracted instead.
  //
  // Except when one is ON. A relay marked out of use that still carries a
  // schedule row is still driven by the hardware, and hiding an energised valve
  // is a worse failure than showing a channel nobody wants.
  //
  // On a bridge-timed site the bitmask alone cannot make that judgement — it is
  // only refreshed when somebody asks — so a valve the bridge has a window open
  // on counts as energised too.
  const outOfUse = new Set(router.disabled_relays ?? []);
  const bridgeDriving = (i: number) => {
    const state = bridgeValves?.get(`${router.router_mac}:${i}`)?.state;
    return state === 'running' || state === 'due' || state === 'did-not-start';
  };
  const shownRelays = Array.from({ length: relayCount }, (_, i) => i)
    .filter(i => !outOfUse.has(i) || relayStates[i] || bridgeDriving(i));
  const hiddenRelayCount = relayCount - shownRelays.length;

  const schedByRelay: Record<number, RelayScheduleState> = {};
  schedule?.relays.forEach(r => { schedByRelay[r.relay] = r; });
  const armedCount = schedule?.relays.filter(r => r.runnable).length ?? 0;
  const skew = schedule ? clockSkewSec(schedule.secs_today, schedule.reported_at, timezoneOffsetSec) : null;
  const skewBad = skew !== null && Math.abs(skew) > 120;

  function saveLabels() {
    // A node that reports its own count owns the field - sending a value back
    // would be overwritten by its next announce anyway. Only offer the operator
    // the setting where the hardware has nothing to say (every XBee node).
    //
    // disabled_relays is always sent: no node can report which of its channels
    // somebody has wired up, so the operator's answer is the only answer, on
    // every transport.
    //
    // Clamped to the relays the board now has. Shrinking the count with relay 6
    // marked out of use would otherwise leave a hidden entry that reappears the
    // day somebody widens the board again.
    const kept = [...disabledValue].filter(i => i < editRelayCount).sort((a, b) => a - b);
    onSaveLabels(labels, reportsOwnCount ? undefined : countValue, kept);
    setEditingLabels(false);
  }

  function saveRouterName() {
    onRename(nameValue.trim());
    setEditingName(false);
  }

  // Deliberately NOT dimmed when the node did not answer. A faded card reads as
  // "nothing here works", which is the opposite of true: a node that has gone
  // quiet is precisely the one somebody is standing in front of with a
  // multimeter. The status dot and its tooltip make the claim; the card does
  // not need to also make the tools look broken.
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            {editingName ? (
              <div className="flex items-center gap-1.5">
                <input
                  autoFocus
                  maxLength={16}
                  value={nameValue}
                  onChange={e => setNameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveRouterName(); if (e.key === 'Escape') setEditingName(false); }}
                  className="border border-gray-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-32"
                />
                <button onClick={saveRouterName} className="text-xs text-blue-600 hover:underline">Save</button>
                <button onClick={() => setEditingName(false)} className="text-xs text-gray-400 hover:underline">Cancel</button>
              </div>
            ) : (
              <CardTitle className="flex items-center gap-2 text-base">
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    unverified ? 'bg-amber-400'
                      : failed ? 'bg-red-500' : 'bg-green-500'
                  }`}
                  title={
                    stateUnknown
                      ? `State unknown — the coordinator is disconnected, so this is the last value read${router.last_seen ? ` (${formatDateTimeInTz(router.last_seen, orgTz)})` : ''}`
                      : unverified
                        // The honest version of what used to be a red dot: this
                        // node has not been asked, so nothing is being claimed.
                        // Watering it, or running Find nodes, settles it.
                        ? `Not verified — nothing heard from this ${noun.oneLower} recently and nothing has asked it. It has NOT failed; use it or run "Find ${noun.many}" to check`
                        : failed
                          ? `Did not answer — a command or check was sent to this ${noun.oneLower} and got no reply${router.last_seen ? `. Last heard ${formatDateTimeInTz(router.last_seen, orgTz)}` : ''}`
                          : 'Online'}
                />
                <button
                  onClick={() => { setNameValue(router.name ?? ''); setEditingName(true); }}
                  className="hover:text-blue-600 transition-colors text-left"
                  title="Rename router"
                >
                  {router.name || router.router_mac.slice(-6)}
                </button>
                {isMasterRouter && <span className="text-amber-500 text-sm" title="Contains master pump relay">⚡</span>}
              </CardTitle>
            )}
            <p className="text-xs text-gray-400 mt-0.5">
              {router.router_mac}
              {router.rssi_dbm !== null && ` · ${router.rssi_dbm} dBm`}
              {router.device_id !== null && ` · ID ${router.device_id}`}
              {router.device_type && ` · ${router.device_type}`}
              {router.firmware_version != null && ` · fw v${router.firmware_version}`}
              {router.transport && ` · ${router.transport.toUpperCase()}`}
              {router.transport === 'lora' && router.lora_provisioned === false && (
                <span className="text-amber-500" title="LoRa not provisioned"> · unprovisioned</span>
              )}
            </p>
          </div>
          <div className="text-right text-xs text-gray-400 flex-shrink-0 flex items-center gap-2">
            {activeCount > 0 && (
              <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                {activeCount} active
              </span>
            )}
            {isSuperAdmin && (
              <button
                onClick={onUpdateFirmware}
                disabled={otaBusy}
                /* An OTA is queued at the master, not handed to the node, so an
                   unreachable node is no reason to lock it: a node that dropped
                   off mid-update is one of the few this is actually for. */
                title={failed || unverified
                  ? `Queue an OTA firmware update — this ${noun.oneLower} is not answering, so it will apply whenever it comes back`
                  : 'Queue OTA firmware update for this router'}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload className={`w-3 h-3 ${otaBusy ? 'animate-pulse' : ''}`} />
                {otaBusy ? 'Queuing…' : 'Update'}
              </button>
            )}
            {/* Deliberately NOT disabled when the node is unreachable: the usual
                reason to remove one is that it is broken, so requiring it to
                answer first would lock the button exactly when it is wanted. */}
            {canRemove && (
              <button
                onClick={onRemove}
                title={`Remove this ${noun.oneLower} from the controller`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-3 h-3" />
                Remove
              </button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {/* A node with no clock has a dead scheduler — every stored program on
            it is inert. That outranks anything the individual tiles can say. */}
        {schedule && !schedule.clock_ok && (
          <div className="flex items-start gap-1.5 p-2 bg-red-50 border border-red-200 rounded-lg text-[11px] text-red-800">
            <ClockAlert className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
            <span>
              <span className="font-medium">No clock.</span> This node has had no time sync since boot,
              so its scheduler is a no-op — nothing here will water.
            </span>
          </div>
        )}
        {schedule && schedule.clock_ok && skewBad && (
          <div className="flex items-start gap-1.5 p-2 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-800">
            <ClockAlert className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
            <span>
              <span className="font-medium">Clock is {formatDuration(Math.abs(skew!))} {skew! > 0 ? 'ahead' : 'behind'}.</span>{' '}
              Programs will start at the wrong time — node reads {formatSecsOfDay(schedule.secs_today!)}.
            </span>
          </div>
        )}

        {/* Relay tiles — click to toggle (test). Each carries the node's own
            answer to "will this water?" beneath the live on/off state. */}
        <div className="grid grid-cols-4 gap-1.5">
          {shownRelays.map(i => {
            // What the node last reported. A tap in flight is tracked beside it
            // rather than written over it — the two disagreeing is the single
            // most useful thing this tile can say.
            const on = relayStates[i];
            const cmd = relayCommands.get(`${router.router_mac}:${i}`) ?? null;
            const busy = relayCommandBusy(cmd);
            // What the NEXT tap should ask for. Not the bitmask: on a site whose
            // outputs are only read when asked, the bitmask can still be showing
            // the state from before the tap, and tapping "off" would re-send "on".
            const intended = relayIntendedState(cmd, on, nowMs);
            const isMasterRelay = pumpsOnThisRouter.some(p => p.relay_index === i);
            const label = labels[String(i)] || `Relay ${i}`;
            // Enabled whatever the NODE's state - unverified or failed alike.
            // "Did not answer the last command" is history, not a prediction:
            // the node may have been power-cycled, re-seated or driven out to
            // since, and the only way to find out is to send it something.
            // Disabling these told the operator a node had failed AND took away
            // the one control that would have shown otherwise, exactly when
            // they were trying to fix it. The red dot carries the claim.
            //
            // The CONTROLLER being offline is different: nothing leaves the
            // server, and a queued valve command firing unattended whenever the
            // bridge next reconnects is its own hazard. That stays disabled.
            //
            // A tap already in flight on THIS relay is blocked; one on a
            // neighbour is not. Following a command through to the node's own
            // reading takes a minute or more, and locking the whole card for it
            // would make the confirmation cost more than it is worth.
            const disabled = !controllerOnline || busy;
            const sched = schedByRelay[i];
            // A node that holds its own timers is its own best witness, so its
            // snapshot wins. The bridge's timeline answers for every valve it
            // does not cover — on an XBee site that is all of them.
            const bridge = sched ? null : bridgeValves?.get(`${router.router_mac}:${i}`) ?? null;
            // A window the bridge has opened is the same claim a tap makes and
            // gets the same amber, until the node's own reading turns it green
            // — or red, if the node says the output is shut.
            const tile = withScheduledTone(
              relayTile(cmd, on, nowMs, router.relay_state_source ?? null),
              bridge?.state,
            );

            // `runnable` is computed by the firmware — never re-derive it here.
            const wontRun = sched ? sched.enabled && !sched.runnable : false;
            // "armed" is the node's word and stays the node's word — nothing is
            // armed on an XBee node. A bridge-timed valve says what the bridge's
            // clock says about it instead.
            const bridgeNote = !bridge
              ? null
              : bridge.state === 'running' && bridge.remainingSec != null
              ? `${formatCountdown(Math.max(0, bridge.remainingSec))} left`
              : bridge.state === 'did-not-start'
              ? 'did not start'
              : bridge.state === 'due'
              ? 'due now'
              : bridge.state === 'skipped'
              ? 'skipped'
              : bridge.state === 'upcoming' && bridge.startsInSec != null
              ? `in ${formatCountdown(bridge.startsInSec)}`
              : null;
            const scheduleNote = !sched
              ? bridgeNote
              : wontRun
              ? "won't run"
              : sched.runnable && sched.next_run_in_sec != null
              ? `in ${formatCountdown(sched.next_run_in_sec)}`
              : sched.runnable
              ? 'armed'
              : null;

            const bridgeTitle = !bridge
              ? ''
              : bridge.state === 'running'
              ? `\nWatering now — ${formatDuration(Math.max(0, bridge.remainingSec ?? 0))} left of ${formatDuration(bridge.durationSec ?? 0)} (bridge clock)`
              : bridge.state === 'did-not-start'
              ? `\nThe schedule opened this valve ${formatDuration(bridge.elapsedSec ?? 0)} ago and the ${noun.oneLower} reports the output shut`
              : bridge.state === 'due'
              ? `\nScheduled to be watering now — ${formatDuration(Math.max(0, bridge.remainingSec ?? 0))} left.`
                + `\nThe ${noun.oneLower} has not reported its outputs since the window opened, so whether it is open is unknown.`
              : bridge.state === 'skipped'
              ? `\nThe sequence that would have opened this valve was skipped`
              : bridge.state === 'upcoming' && bridge.startsInSec != null
              ? `\nScheduled in ${formatCountdown(bridge.startsInSec)} for ${formatDuration(bridge.durationSec ?? 0)} (bridge clock)`
              : '';
            const scheduleTitle = !sched
              ? bridgeTitle
              : wontRun
              ? `\nStored but will not run${sched.reason ? ` — ${sched.reason}` : ''}` +
                `\nProgram: ${formatSecsOfDay(sched.start_sec)} for ${formatDuration(sched.duration_sec)}`
              : sched.runnable
              ? `\nArmed: ${formatSecsOfDay(sched.start_sec)} for ${formatDuration(sched.duration_sec)} (node clock)`
              : `\nNo program set`;

            // What the last tap has got as far as. Its own sentence, because
            // "the site says it did it" and "the node says the output moved"
            // are different facts and the operator has to be able to tell which
            // one is on screen.
            const cmdTitle = !cmd || !tile.note
              ? ''
              : cmd.detail
              ? `\n${cmd.detail}`
              : cmd.phase === 'acked'
              ? `\nThe site carried the command out. Reading the ${noun.oneLower}'s outputs back to confirm it…`
              : cmd.phase === 'confirming'
              ? `\nWaiting for the ${noun.oneLower} to report its outputs`
              : cmd.phase === 'confirmed'
              ? `\nThe ${noun.oneLower} reports this output ${cmd.want ? 'on' : 'off'}`
              : '';

            // A failed node still gets the normal instruction, prefixed with
            // what is known about it - the warning belongs alongside the
            // action, not instead of it.
            const failedPrefix = failed
              ? `${noun.one} did not answer the last command sent to it — worth trying anyway. `
              : '';
            const tooltip = (!controllerOnline
              ? 'Controller offline'
              : busy
              ? 'Sending command…'
              : failedPrefix + (intended
                ? `Click to turn off ${label}`
                : `Click to turn on ${label} — click again to stop it, or it stops itself after ${MANUAL_RUN_MAX_LABEL}`)) + cmdTitle + scheduleTitle;

            return (
              <button
                type="button"
                key={i}
                onClick={() => onToggleRelay(i, intended)}
                disabled={disabled}
                title={tooltip}
                className={`rounded-lg px-1.5 py-2 text-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  RELAY_TONE[tile.tone]
                } ${isMasterRelay ? 'border-2 border-amber-400' : ''} ${
                  wontRun && tile.tone === 'off' ? 'ring-1 ring-red-300' : ''
                } ${busy ? 'animate-pulse' : ''}`}
              >
                <div className="text-xs font-medium truncate" title={label}>{label.slice(0, 6)}</div>
                <div className={`text-xs mt-0.5 ${tile.tone === 'off' ? 'opacity-60' : 'opacity-90'}`}>
                  {tile.state}
                </div>
                {/* A tap in progress owns this line. What the schedule intends
                    can wait a minute; what the valve just did cannot. */}
                {(tile.note ?? scheduleNote) && (
                  <div
                    className={`text-[10px] mt-0.5 leading-tight truncate ${
                      tile.note
                        ? tile.tone === 'off' ? 'text-gray-500' : 'opacity-80'
                        : tile.tone !== 'off'
                        ? 'opacity-80'
                        : wontRun || bridge?.state === 'did-not-start'
                        ? 'text-red-600 font-medium'
                        : bridge?.state === 'due' || bridge?.state === 'skipped'
                        ? 'text-amber-600 font-medium'
                        : 'text-gray-400'
                    }`}
                  >
                    {tile.note ?? scheduleNote}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-gray-400 italic">
          Tip: tap a relay to turn it on, tap it again to turn it off. Test runs
          auto-stop after 60 s.
        </p>
        {hiddenRelayCount > 0 && (
          <p className="text-[11px] text-gray-400">
            {hiddenRelayCount === 1 ? '1 relay is' : `${hiddenRelayCount} relays are`} marked as
            not in use and hidden — edit relay labels to change that.
          </p>
        )}

        {/* What the node itself last told us. Sourced from the device, so it
            stays true across reboots, NVS wipes and third-party masters. */}
        {schedule && (
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-gray-400 border-t border-gray-100 pt-2">
            <Clock className="w-3 h-3 flex-shrink-0" />
            <span className={schedule.clock_ok ? '' : 'text-red-500 font-medium'}>
              node clock{' '}
              {!schedule.clock_ok
                ? 'not set'
                : schedule.secs_today != null
                ? formatSecsOfDay(schedule.secs_today)
                : 'not reported'}
            </span>
            <span>·</span>
            <span>{armedCount} of {schedule.relay_count} armed</span>
            {schedule.reported_at && (
              <>
                <span>·</span>
                <span title={schedule.reported_at}>
                  reported {formatDateTimeInTz(schedule.reported_at, orgTz)}
                </span>
              </>
            )}
          </div>
        )}

        {/* Label editor */}
        {editingLabels ? (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-gray-500">Edit Relay Labels</p>
            {!reportsOwnCount && (
              <div className="flex items-center gap-2">
                <label htmlFor={`relay-count-${router.router_mac}`} className="text-xs text-gray-400">
                  Relays on this board
                </label>
                <select
                  id={`relay-count-${router.router_mac}`}
                  value={countValue ?? ''}
                  onChange={e => setCountValue(e.target.value === '' ? null : parseInt(e.target.value))}
                  className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {/* Blank = not set, so the transport default applies. */}
                  <option value="">Auto ({relayCountFor({ transport: router.transport })})</option>
                  {[1, 2, 3, 4, 6, 8].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}
            {/* "In use" is a separate question from how many relays the board
                has, and both have to be askable. A board wired on 0 and 3 must
                still say it has 4 - dropping the count to 2 would make channel
                3 unaddressable - so the unused ones are ticked off instead, and
                every picker stops offering them. */}
            <p className="text-[11px] text-gray-400">
              Untick a relay that is not wired to anything. It stops appearing in
              the schedule and pump pickers, but keeps its channel number.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {Array.from({ length: editRelayCount }, (_, i) => {
                const inUse = !disabledValue.has(i);
                return (
                  <div key={i} className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      id={`relay-in-use-${router.router_mac}-${i}`}
                      checked={inUse}
                      onChange={e => setDisabledValue(prev => {
                        const next = new Set(prev);
                        if (e.target.checked) next.delete(i); else next.add(i);
                        return next;
                      })}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                    />
                    <label
                      htmlFor={`relay-in-use-${router.router_mac}-${i}`}
                      className="text-xs text-gray-400 w-12 flex-shrink-0 cursor-pointer"
                      title={inUse ? `Relay ${i} is in use` : `Relay ${i} is not wired to anything`}
                    >
                      Relay {i}
                    </label>
                    <input
                      type="text"
                      maxLength={20}
                      placeholder={inUse ? `Relay ${i}` : 'Not in use'}
                      disabled={!inUse}
                      value={labels[String(i)] ?? ''}
                      onChange={e => setLabels(prev => ({ ...prev, [String(i)]: e.target.value }))}
                      className="flex-1 min-w-0 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2">
              <button onClick={saveLabels} className="text-xs text-blue-600 hover:underline">Save Labels</button>
              <button onClick={() => setEditingLabels(false)} className="text-xs text-gray-400 hover:underline">Cancel</button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setEditingLabels(true)}
            className="text-xs text-blue-600 hover:underline"
          >
            <Settings className="w-3 h-3 inline mr-0.5" /> Edit relay labels
          </button>
        )}

        {/* One node, asked on its own. XBee only, for the same reason the
            diagnostics panel is: the endpoint refuses anything else, and an
            ESP-NOW master would ack it as an unsupported command a minute
            later. Hidden for the master's own on-board relay bank too — there
            is no radio link between the controller and itself to measure. */}
        {transport === 'xbee' && !router.is_local && (
          <NodeSelfTest
            controllerId={controllerId}
            router={router}
            transport={transport}
            controllerOnline={controllerOnline}
            onFinished={onTestFinished}
          />
        )}
      </CardContent>
    </Card>
  );
}
